/**
 * HistoryFilterBar — two dropdowns: type + period.
 *
 * Figma sources:
 *   - 319:5771  history screen filter row.
 *   - 319:6393  open-state dropdown panel:
 *       width 179, height 160 (HUG vertical),
 *       fill `surface/2 lvl` (#121214),
 *       cornerRadius 16,
 *       padding 20/16/20/16, itemSpacing 12,
 *       stroke = gradient grey 0→0.48 (1px),
 *       drop shadow 0/4/16 rgba(0,0,0,0.24).
 *
 *     Each row is HORIZONTAL gap=12.
 *     Selected row text → `icon/accent` (#FFAC0B, orange) + trailing
 *     `check` icon 16×16 white.
 *     Unselected text → `text/secondary` (#C9C9C9) + no trailing icon.
 *     All labels: Raleway SemiBold 12, letterSpacing -2%.
 *
 * Acceptance §2.4:
 *  - Two dropdowns applied immediately on selection.
 *  - Only one dropdown open at a time.
 *
 * Backend type values  (backend/routes/history.js VALID_TYPES):
 *   'all' | 'generations' | 'photo' | 'video' |
 *   'payment' | 'credit' | 'referral'.
 *
 * Backend period values (backend/routes/history.js VALID_PERIODS):
 *   'all' | '7d' | '30d' | '1y'.
 *
 * NOTE: acceptance §2.4 labels map:
 *   «Покупки»  → type='credit'
 *   «Тарифы»   → type='payment'
 *   «Рефералы» → type='referral'
 *   «Последний месяц» → period='30d'.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../primitives';

export const TYPE_OPTIONS = [
  { value: 'all', labelKey: 'history.filterAll' },
  { value: 'generations', labelKey: 'history.filterGeneration' },
  { value: 'credit', labelKey: 'history.filterPurchase' },
  { value: 'payment', labelKey: 'history.filterSubscription' },
  { value: 'referral', labelKey: 'history.filterReferral' },
];

export const PERIOD_OPTIONS = [
  { value: 'all', labelKey: 'history.periodAll' },
  { value: '7d', labelKey: 'history.period7d' },
  { value: '30d', labelKey: 'history.period30d' },
  { value: '1y', labelKey: 'history.period365d' },
];

/**
 * Single filter dropdown.
 *
 * Trigger pill (FILL × HUG, rounded-home-12, px-3 h-9, surface-2):
 *   - label (truncate, Raleway SemiBold 12)
 *   - chevron-down (or chevron-up when open) on the right.
 *
 * Dropdown panel — see Figma 319:6393 above.
 */
function FilterDropdown({
  value,
  options,
  onChange,
  open,
  onOpen,
  onClose,
  testId,
}) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  return (
    <div ref={ref} className="relative flex-1 min-w-0" data-testid={testId}>
      <button
        type="button"
        onClick={open ? onClose : onOpen}
        className="w-full flex items-center justify-between gap-1 px-3 h-9 rounded-home-12 bg-surface-2 text-text-primary font-sans font-semibold text-[12px] leading-none tracking-[-0.24px]"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{t(selected.labelKey)}</span>
        <Icon
          name={open ? 'chevron-up' : 'chevron-down'}
          size={14}
          className="text-text-secondary shrink-0"
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute left-0 top-full mt-1 w-full z-50 rounded-home-16 bg-surface-2 overflow-hidden flex flex-col gap-3 px-4 py-5"
          style={{
            boxShadow: '0 4px 16px 0 rgba(0,0,0,0.24)',
            border: '1px solid rgba(102,102,102,0.24)',
          }}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value);
                  onClose();
                }}
                className={[
                  'flex items-center justify-between gap-3 cursor-pointer',
                  'font-sans font-semibold text-[12px] leading-none tracking-[-0.24px]',
                  isSelected ? 'text-icon-accent' : 'text-text-secondary',
                ].join(' ')}
              >
                <span className="truncate">{t(opt.labelKey)}</span>
                {isSelected && (
                  <Icon name="check" size={16} className="text-text-primary shrink-0" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * HistoryFilterBar
 *
 * Props:
 *   type           — current type filter value (backend param).
 *   period         — current period filter value (backend param).
 *   onTypeChange   — (value: string) => void.
 *   onPeriodChange — (value: string) => void.
 */
export default function HistoryFilterBar({
  type,
  period,
  onTypeChange,
  onPeriodChange,
}) {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <div
      data-testid="history-filter-bar"
      className="flex items-center gap-2 px-4 pt-4 pb-2"
    >
      <FilterDropdown
        testId="filter-type"
        value={type}
        options={TYPE_OPTIONS}
        onChange={onTypeChange}
        open={openDropdown === 'type'}
        onOpen={() => setOpenDropdown('type')}
        onClose={() => setOpenDropdown(null)}
      />
      <FilterDropdown
        testId="filter-period"
        value={period}
        options={PERIOD_OPTIONS}
        onChange={onPeriodChange}
        open={openDropdown === 'period'}
        onOpen={() => setOpenDropdown('period')}
        onClose={() => setOpenDropdown(null)}
      />
    </div>
  );
}
